import { go } from "@codemirror/lang-go";
import { markdown } from "@codemirror/lang-markdown";
import { yaml } from "@codemirror/lang-yaml";
import { cn, useThemeStore } from "@theprelude/core";
import {
  CopyIcon,
  DownloadIcon,
  handleCopy,
  IconButton,
  useDownloadFile,
} from "@theprelude/ds";
import { quietlight } from "@uiw/codemirror-theme-quietlight";
import ReactCodeMirror, {
  EditorView,
  Extension,
  oneDark,
  ReactCodeMirrorProps,
} from "@uiw/react-codemirror";
import { DefaultTooltip } from "../default-tooltip";

type FileTypeInfo = {
  mimeType: `${string}/${string}`;
  name: string;
  extensions: Extension[];
  ext?: string;
};

const fileTypes: Record<"txt" | "go" | "yaml" | "md", FileTypeInfo> = {
  txt: {
    mimeType: "text/plain",
    name: "Text",
    extensions: [],
    ext: ".txt",
  },
  go: {
    mimeType: "text/go",
    name: "Go",
    extensions: [go()],
  },
  yaml: {
    mimeType: "text/yaml",
    name: "YAML",
    extensions: [yaml()],
    ext: ".yml",
  },
  md: {
    mimeType: "text/markdown",
    name: "Markdown",
    extensions: [markdown()],
    ext: ".md",
  },
};

type FileType = keyof typeof fileTypes;

type EditorProps = ReactCodeMirrorProps & {
  type?: FileType;
  editable?: boolean;
  showName?: boolean;
  filename?: string;
};

const defaultExtensions = [EditorView.lineWrapping];

export default function Editor({
  type = "txt",
  filename,
  extensions = defaultExtensions,
  editable = false,
  showName = true,
  ...props
}: EditorProps) {
  const fileType = fileTypes[type];
  const theme = useThemeStore((state) => state.theme ?? "dark");
  const currentTheme = theme === "light" ? quietlight : oneDark;
  const displayName = filename ?? `untitled${fileType?.ext}`;

  const download = useDownloadFile({
    data: props.value ?? "",
    filename: displayName,
    type: fileType?.mimeType,
  });

  return (
    <div className="relative">
      <div className="flex h-[37px] flex-row items-center justify-between bg-[#F5F5F5] px-2 py-2 dark:bg-[#282c34]">
        <div>{showName && displayName}</div>
        <div className="flex gap-3">
          <DefaultTooltip content="Download" asChild>
            <IconButton
              type="button"
              onClick={download.execute}
              icon={
                <DownloadIcon className="text-secondary-foreground hover:text-primary-foreground" />
              }
            />
          </DefaultTooltip>

          <DefaultTooltip content="Copy" asChild>
            <IconButton
              type="button"
              onClick={() => {
                void handleCopy(props.value ?? "");
              }}
              icon={
                <CopyIcon className="text-secondary-foreground hover:text-primary-foreground" />
              }
            />
          </DefaultTooltip>
        </div>
      </div>
      <ReactCodeMirror
        height="300px"
        editable={editable}
        theme={currentTheme}
        extensions={[...fileType.extensions, ...extensions]}
        {...props}
      />

      <div
        className={cn(
          "flex flex-row items-center justify-between px-2 py-2 text-xs",
          {
            "bg-[#F5F5F5] dark:bg-[#282c34]": !editable,
            "bg-[#79ff002b]": editable,
          },
        )}
      >
        <div>{editable ? "Editing" : "Viewing"}</div>
        <div className="font-semibold">{fileType.name}</div>
      </div>
    </div>
  );
}
